import { useState } from 'react';
import type { MouseEvent } from 'react';
import {
  CloudUploadOutlined,
  CreateNewFolderOutlined,
  DeleteOutline,
  EditOutlined,
  MoreVert,
} from '@mui/icons-material';
import Divider from '@mui/material/Divider';
import {
  PrimaryNavigationMenu,
  PrimaryNavigationMenuItem,
} from 'components/ui';
import { useZoom } from 'context/ZoomContext';
import { FIRM_DOCUMENT_FOLDER_ACTIONS_LABELS } from './FirmDocumentFolderActions.constants';
import {
  FirmDocumentFolderActionsRoot,
  FirmDocumentFolderActionTrigger,
} from './FirmDocumentFolderActions.styles';
import { FIRM_DOCUMENT_FOLDER_ACTIONS_TEST_IDS } from './FirmDocumentFolderActions.testIds';
import type { FirmDocumentFolderActionsProps } from './FirmDocumentFolderActions.types';

const FirmDocumentFolderActions = ({
  actionInProgress = false,
  folder,
  onAddSubfolder,
  onDelete,
  onRename,
  onUploadDocument,
}: FirmDocumentFolderActionsProps) => {
  const { zoomLevel } = useZoom();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const canAddSubfolder = Boolean(onAddSubfolder && folder.md_documents);
  const canUploadDocument = Boolean(onUploadDocument && folder.md_documents);
  const canRenameFolder = Boolean(onRename && !folder.isCompanyGroup && folder.md_documents);
  const canDeleteFolder = Boolean(onDelete && !folder.isCompanyGroup && folder.md_documents);
  const isDisabled = actionInProgress || (!canAddSubfolder && !canUploadDocument && !canRenameFolder && !canDeleteFolder);
  const isMenuOpen = Boolean(anchorEl);
  const menuId = `firm-documents-folder-actions-menu-${folder.id}`;
  const shouldShowDeleteDivider = canDeleteFolder && Boolean(onAddSubfolder || onUploadDocument || canRenameFolder);
  const handleTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleAddSubfolderClick = (event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    handleMenuClose();
    onAddSubfolder?.(folder);
  };
  const handleRenameClick = (event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    handleMenuClose();
    window.setTimeout(() => onRename?.(folder));
  };
  const handleUploadDocumentClick = (event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    handleMenuClose();
    onUploadDocument?.(folder);
  };
  const handleDeleteClick = (event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    handleMenuClose();
    onDelete?.(folder);
  };

  return (
    <FirmDocumentFolderActionsRoot
      aria-label={FIRM_DOCUMENT_FOLDER_ACTIONS_LABELS.trigger(folder.name)}
      data-row-action
      data-testid={FIRM_DOCUMENT_FOLDER_ACTIONS_TEST_IDS.root(folder.id)}
      isMenuOpen={isMenuOpen}
      role="group"
      zoomLevel={zoomLevel}>
      <FirmDocumentFolderActionTrigger
        aria-controls={isMenuOpen ? menuId : undefined}
        aria-expanded={isMenuOpen || undefined}
        aria-haspopup="menu"
        aria-label={FIRM_DOCUMENT_FOLDER_ACTIONS_LABELS.trigger(folder.name)}
        data-testid={FIRM_DOCUMENT_FOLDER_ACTIONS_TEST_IDS.trigger(folder.id)}
        disabled={isDisabled}
        onClick={handleTriggerClick}
        size="small"
        tooltipLabel={FIRM_DOCUMENT_FOLDER_ACTIONS_LABELS.trigger(folder.name)}
        variant="ghost"
        visualSize="compact">
        <MoreVert fontSize="inherit" />
      </FirmDocumentFolderActionTrigger>
      <PrimaryNavigationMenu
        anchorEl={anchorEl}
        disableRestoreFocus
        id={menuId}
        menuMinWidth={0}
        menuWidth="content"
        onClick={event => event.stopPropagation()}
        onClose={handleMenuClose}
        open={isMenuOpen}>
        {onAddSubfolder ? (
          <PrimaryNavigationMenuItem
            data-testid={FIRM_DOCUMENT_FOLDER_ACTIONS_TEST_IDS.addSubfolderAction(folder.id)}
            disabled={actionInProgress || !canAddSubfolder}
            leadingSlot={<CreateNewFolderOutlined fontSize="inherit" />}
            onClick={handleAddSubfolderClick}>
            {FIRM_DOCUMENT_FOLDER_ACTIONS_LABELS.addSubfolder}
          </PrimaryNavigationMenuItem>
        ) : null}
        {onUploadDocument ? (
          <PrimaryNavigationMenuItem
            data-testid={FIRM_DOCUMENT_FOLDER_ACTIONS_TEST_IDS.uploadDocumentAction(folder.id)}
            disabled={actionInProgress || !canUploadDocument}
            leadingSlot={<CloudUploadOutlined fontSize="inherit" />}
            onClick={handleUploadDocumentClick}>
            {FIRM_DOCUMENT_FOLDER_ACTIONS_LABELS.uploadDocument}
          </PrimaryNavigationMenuItem>
        ) : null}
        {canRenameFolder ? (
          <PrimaryNavigationMenuItem
            data-testid={FIRM_DOCUMENT_FOLDER_ACTIONS_TEST_IDS.renameAction(folder.id)}
            disabled={actionInProgress}
            leadingSlot={<EditOutlined fontSize="inherit" />}
            onClick={handleRenameClick}>
            {FIRM_DOCUMENT_FOLDER_ACTIONS_LABELS.rename}
          </PrimaryNavigationMenuItem>
        ) : null}
        {canDeleteFolder ? (
          [
            shouldShowDeleteDivider ? <Divider key="delete-divider" /> : null,
            <PrimaryNavigationMenuItem
              key="delete"
              data-testid={FIRM_DOCUMENT_FOLDER_ACTIONS_TEST_IDS.deleteAction(folder.id)}
              disabled={actionInProgress}
              leadingSlot={<DeleteOutline fontSize="inherit" />}
              onClick={handleDeleteClick}
              tone="danger">
              {FIRM_DOCUMENT_FOLDER_ACTIONS_LABELS.delete}
            </PrimaryNavigationMenuItem>,
          ]
        ) : null}
      </PrimaryNavigationMenu>
    </FirmDocumentFolderActionsRoot>
  );
};

export default FirmDocumentFolderActions;
